<script>
import { mapGetters } from "vuex";
import {
  isToday,
  formatDistance,
  parseISO,
  startOfDay,
  endOfDay,
} from "date-fns";
import dateFnsEsp from "date-fns/locale/es";
import pagination from "@/mixins/pagination";

import debounce from "debounce";
import helperService from "@/services/helper";
import userService from "@/services/user";
import institutionService from "@/services/institution";

import Spinner from "@/components/ui/Spinner";
import BaseSubNav from "@/components/ui/BaseSubNav.vue";

const DEBOUNCE_MS_TIMEOUT = 500;

export default {
  name: "DoctorLocationList",

  mixins: [pagination],

  components: {
    Spinner,
    BaseSubNav,
  },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      onlyWithLocation: true,
      doctors: [],
      institutionsById: {},
      hasMultipleInstitutions: false,
    };
  },

  computed: {
    ...mapGetters(["isAdmin", "isAssistant"]),
  },

  created() {
    this.loadInstitutions();
    this.getDoctors();
  },

  methods: {
    loadInstitutions() {
      institutionService.getInstitutions().then((institutions) => {
        this.institutionsById = (institutions || []).reduce((map, inst) => {
          map[inst._id] = inst.name;
          return map;
        }, {});
        this.hasMultipleInstitutions = (institutions || []).length > 1;
      });
    },

    buildSearchQuery() {
      const query = {};

      const searchInput = this.searchInput && this.searchInput.trim();
      if (searchInput) {
        query.$text = {
          $search: searchInput,
        };
      }

      if (this.onlyWithLocation) {
        const now = new Date();
        query["doctor.selectedBoxAt"] = {
          $gte: startOfDay(now),
          $lte: endOfDay(now),
        };
      }

      return query;
    },

    onFilterChange() {
      // Reset to the first page whenever the filter changes.
      this.pagination.offset = 0;

      if (this.pagination.currentPage !== 1) {
        // Triggers the pagination watcher -> onPageChange -> getDoctors.
        this.pagination.currentPage = 1;
      } else {
        this.getDoctors();
      }
    },

    getDoctors(query = {}) {
      this.isLoading = true;

      const defaultQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        ...this.buildSearchQuery(),
        ...query,
      };

      userService
        .getDoctorLocations(defaultQuery)
        .then((res) => {
          this.doctors = res.docs;
          this.pagination.total = res.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    debounceInput: debounce(function () {
      // Reset to the first page whenever a new search is performed.
      this.pagination.offset = 0;

      if (this.pagination.currentPage !== 1) {
        // Triggers the pagination watcher -> onPageChange -> getDoctors.
        this.pagination.currentPage = 1;
      } else {
        this.getDoctors();
      }
    }, DEBOUNCE_MS_TIMEOUT),

    onPageChange() {
      this.getDoctors();
    },

    getFullName(doctor) {
      return helperService.getFullName(doctor);
    },

    hasAttendedToday(doctor) {
      const selectedBoxAt = doctor.doctor?.selectedBoxAt;
      return Boolean(selectedBoxAt) && isToday(parseISO(selectedBoxAt));
    },

    boxName(doctor) {
      if (!this.hasAttendedToday(doctor)) {
        return "Sin atención hoy";
      }
      return doctor.doctor?.selectedBox?.name || "Sin box asignado";
    },

    statusBadgeClass(doctor) {
      return this.hasAttendedToday(doctor) ? "is-active" : "is-inactive";
    },

    institutionName(doctor) {
      if (!this.hasAttendedToday(doctor)) {
        return "";
      }
      const institutionId = doctor.doctor?.selectedBox?.institution;
      return institutionId ? this.institutionsById[institutionId] || "" : "";
    },

    timeSinceLabel(doctor) {
      if (!this.hasAttendedToday(doctor)) {
        return "-";
      }
      return formatDistance(parseISO(doctor.doctor.selectedBoxAt), new Date(), {
        addSuffix: true,
        locale: dateFnsEsp,
      });
    },
  },
};
</script>

<template lang="pug">
section.doctor-location-list
  BaseSubNav
    template(#title)
      | Ubicación médicos
      small(v-if="pagination.total")  ({{ pagination.total }})
    template(#actions)
      el-checkbox(
        v-model="onlyWithLocation"
        @change="onFilterChange"
      ) Solo con ubicación hoy
      el-input.search__input(
        prefix-icon="el-icon-search"
        v-on:input="debounceInput"
        v-model="searchInput"
        placeholder="Buscar por nombre o apellido"
        :clearable="true"
      )
  .box
    .box__content--stretch
      table
        thead
          tr
            th Doctor
            th Box
            th(v-if="hasMultipleInstitutions") Institución
            th Actualizado
        tbody
          spinner(v-if="isLoading")
          tr(v-else v-for="doctor in doctors" :key="doctor._id")
            td
              b {{ getFullName(doctor) }}
            td
              span.status-badge(:class="statusBadgeClass(doctor)") {{ boxName(doctor) }}
            td(v-if="hasMultipleInstitutions") {{ institutionName(doctor) }}
            td {{ timeSinceLabel(doctor) }}

    pagination(
      :isLoading="isLoading"
      :limit="pagination.limit"
      :total="pagination.total"
      @pagination="setPagination"
    )
</template>

<style lang="scss" scoped>
tbody td {
  vertical-align: middle;
}

small {
  margin-right: 5px;
  font-size: 10px;
  font-weight: 700;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;

  &.is-active {
    background-color: #f0f9eb;
    color: #67c23a;
  }

  &.is-inactive {
    background-color: #f4f4f5;
    color: #909399;
  }
}
</style>
